import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useWebSocketContext, EventType } from '../contexts/WebSocketContext';
import type { WebSocketEvent } from './useWebSocket';
import { agentKeys } from './useAgents';
import type { Agent } from '../types/agent';

/**
 * Event payload types for agent events
 */
export interface AgentCreatedPayload {
  agent: Agent;
}

export interface AgentUpdatedPayload {
  agent: Agent;
  changes: string[];
}

export interface AgentDeletedPayload {
  agent_id: string;
  name: string;
}

export interface AgentStatusChangedPayload {
  agent_id: string;
  name: string;
  old_status: string;
  new_status: string;
}

/**
 * Options for the useAgentEvents hook
 */
export interface UseAgentEventsOptions {
  /** Whether to show toast notifications (default: true) */
  showToasts?: boolean;
  /** Callback when an agent is created */
  onAgentCreated?: (payload: AgentCreatedPayload) => void;
  /** Callback when an agent is updated */
  onAgentUpdated?: (payload: AgentUpdatedPayload) => void;
  /** Callback when an agent is deleted */
  onAgentDeleted?: (payload: AgentDeletedPayload) => void;
  /** Callback when an agent's status changes */
  onAgentStatusChanged?: (payload: AgentStatusChangedPayload) => void;
}

/**
 * Hook to subscribe to agent-related WebSocket events
 *
 * Automatically invalidates React Query cache and shows toast notifications
 * when agent events are received.
 *
 * @param options - Configuration options
 *
 * @example
 * ```tsx
 * function AgentList() {
 *   useAgentEvents({
 *     showToasts: true,
 *     onAgentCreated: (payload) => {
 *       console.log('New agent:', payload.agent.name);
 *     },
 *   });
 *
 *   // Your component logic...
 * }
 * ```
 */
export function useAgentEvents(options: UseAgentEventsOptions = {}): void {
  const {
    showToasts = true,
    onAgentCreated,
    onAgentUpdated,
    onAgentDeleted,
    onAgentStatusChanged,
  } = options;

  const { subscribe, isConnected } = useWebSocketContext();
  const queryClient = useQueryClient();

  /**
   * Handle agent.created event
   */
  const handleAgentCreated = useCallback(
    (event: WebSocketEvent) => {
      const payload = event.payload as unknown as AgentCreatedPayload;

      // Invalidate agent list and stats queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });

      // Show toast notification
      if (showToasts) {
        toast.success(`New agent created: ${payload.agent?.name || 'Unknown'}`, {
          icon: '🤖',
          duration: 4000,
        });
      }

      // Call custom callback
      onAgentCreated?.(payload);
    },
    [queryClient, showToasts, onAgentCreated]
  );

  /**
   * Handle agent.updated event
   */
  const handleAgentUpdated = useCallback(
    (event: WebSocketEvent) => {
      const payload = event.payload as unknown as AgentUpdatedPayload;
      const agentId = event.agent_id || payload.agent?.id;

      // Invalidate agent list and detail queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      if (agentId) {
        queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
      }
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });

      // Show toast notification
      if (showToasts) {
        const changes = payload.changes?.join(', ') || 'configuration';
        toast.success(`Agent "${payload.agent?.name || 'Unknown'}" updated (${changes})`, {
          icon: '✏️',
          duration: 4000,
        });
      }

      // Call custom callback
      onAgentUpdated?.(payload);
    },
    [queryClient, showToasts, onAgentUpdated]
  );

  /**
   * Handle agent.deleted event
   */
  const handleAgentDeleted = useCallback(
    (event: WebSocketEvent) => {
      const payload = event.payload as unknown as AgentDeletedPayload;
      const agentId = event.agent_id || payload.agent_id;

      // Invalidate agent list queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });

      // Remove specific agent from cache
      if (agentId) {
        queryClient.removeQueries({ queryKey: agentKeys.detail(agentId) });
      }

      // Show toast notification
      if (showToasts) {
        toast.success(`Agent "${payload.name || 'Unknown'}" deleted`, {
          icon: '🗑️',
          duration: 4000,
        });
      }

      // Call custom callback
      onAgentDeleted?.(payload);
    },
    [queryClient, showToasts, onAgentDeleted]
  );

  /**
   * Handle agent.status_changed event
   */
  const handleAgentStatusChanged = useCallback(
    (event: WebSocketEvent) => {
      const payload = event.payload as unknown as AgentStatusChangedPayload;
      const agentId = event.agent_id || payload.agent_id;

      // Invalidate agent list and detail queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      if (agentId) {
        queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
      }
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });

      // Show toast notification
      if (showToasts) {
        const isActive = payload.new_status === 'ACTIVE';
        toast.success(
          `Agent "${payload.name || 'Unknown'}" is now ${payload.new_status?.toLowerCase() || 'updated'}`,
          {
            icon: isActive ? '✅' : '⏸️',
            duration: 4000,
          }
        );
      }

      // Call custom callback
      onAgentStatusChanged?.(payload);
    },
    [queryClient, showToasts, onAgentStatusChanged]
  );

  // Subscribe to agent events
  useEffect(() => {
    // Only subscribe when connected
    if (!isConnected) {
      return;
    }

    // Subscribe to all agent events
    const unsubscribeCreated = subscribe(EventType.AGENT_CREATED, handleAgentCreated);
    const unsubscribeUpdated = subscribe(EventType.AGENT_UPDATED, handleAgentUpdated);
    const unsubscribeDeleted = subscribe(EventType.AGENT_DELETED, handleAgentDeleted);
    const unsubscribeStatusChanged = subscribe(
      EventType.AGENT_STATUS_CHANGED,
      handleAgentStatusChanged
    );

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeDeleted();
      unsubscribeStatusChanged();
    };
  }, [
    isConnected,
    subscribe,
    handleAgentCreated,
    handleAgentUpdated,
    handleAgentDeleted,
    handleAgentStatusChanged,
  ]);
}

export default useAgentEvents;
